import React, { useState, useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Alert,
  Table,
  Badge,
  Spinner,
} from "react-bootstrap";
import { Users } from "lucide-react";
import {
  fetchAllTimetable,
  generateAllTimetables,
} from "./redux/timetableSlice";
import { fetchSections } from "../sections/redux/sectionSlice";
import { fetchPeriods } from "../periods/redux/periodSlice";
import userStore from "../../../zustand/userStore";
import TimetableView from "./components/TimetableView";
import { fetchTeachers } from "../teachers/redux/teacherSlice";
import Joyride from "react-joyride";
import { generateTimetableSteps } from "../../../tour/timetableTourSteps";
import { useNavigate } from "react-router-dom";
import { handleJoyRideRunning } from "../subjects/redux/subjectSlice";
import { createPortal } from "react-dom";
import { setStepIndex, startTour, stopTour } from "../../../redux/globalJoyrideSlice";
// import GlobalJoyrideCloseButton from "../../../components/GlobalJoyrideCloseButton";
import "./GenerateTimetableManagement.css";
import { fetchClasses } from "../classes/redux/classSlice";
import {
  getTimetablePrintTitle,
  TimetablePrintPreviewModal,
} from "../PrintDataTable";


const GenerateTimetableManagement = () => {
  const dispatch = useDispatch();

  const { loading, error, timetable: allTimetables } = useSelector(
    (state) => state.timetable
  );

  const { periods } = useSelector((state) => state.periods);
  const masterData = useSelector((state) => state.masterData);
  const { classes } = useSelector((state) => state.classes);
  const { sections } = useSelector((state) => state.sections);
  const schoolId = masterData?.school_id;

  const userData = userStore((state) => state.user);
  const isUserRole = userData?.roles?.toLowerCase() === "user";
  const userId = userData?.id;

  const [selectedSection, setSelectedSection] = useState("");
  const sectionRefs = useRef({});
  const scrollContainerRef = useRef(null);
  const adminId = userData?.admin_id;

  const [selectedClass, setSelectedClass] = useState("ALL");
  const [selectedSectionFilter, setSelectedSectionFilter] = useState("ALL");
  const [showPrintPreview, setShowPrintPreview] = useState(false);

  const navigate = useNavigate();
  // FETCH INITIAL DATA
  useEffect(() => {
    if (schoolId) {
      dispatch(fetchTeachers(schoolId));
      dispatch(fetchClasses(schoolId));
      dispatch(fetchSections(schoolId));
      dispatch(fetchPeriods(schoolId));
      dispatch(fetchAllTimetable(schoolId));
    }
  }, [dispatch, schoolId]);

  // useEffect(() => {
  //   if (adminId === 104) {
  //     setRunTour(true);
  //   } else {
  //     setRunTour(false);
  //   }
  // }, [adminId]);

  useEffect(() => {
    if (!schoolId) return;

    dispatch(
      startTour({
        name: "generateTimetable",
        steps: generateTimetableSteps({}),
      })
    );
  }, [schoolId, dispatch]);


  useEffect(() => {
    if (allTimetables.length && !selectedSection) {
      setSelectedSection(String(allTimetables[0].section_id));
    }
  }, [allTimetables, selectedSection]);


  const handleSectionClick = (sectionId) => {
    setSelectedSection(String(sectionId));

    if (sectionRefs.current[sectionId] && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: sectionRefs.current[sectionId].offsetTop,
        behavior: "smooth",
      });
    }
  };
  const handleGenerateTimetable = async () => {
    if (!schoolId || !userId) {
      alert("School ID or User ID is missing.");
      return;
    }
    dispatch(handleJoyRideRunning(false));

    try {
      await dispatch(
        generateAllTimetables({ school_id: schoolId, user_id: userId })
      ).unwrap();

      alert("Timetables generated successfully!");
      dispatch(fetchAllTimetable(schoolId));
      // setTimeout(() => {
      //   navigate("/timetable/teachertimetable", {
      //     state: { startTeacherTour: true }
      //   });
      // }, 3000);
      dispatch(setStepIndex(1));

    } catch (error) {
      console.error("Error generating timetables:", error);
      alert("Error generating timetables. Please try again.");
    }
  };


  const sectionLookup = useMemo(() => {
    return new Map(
      (Array.isArray(sections) ? sections : []).map((section) => [
        String(section.section_id),
        section,
      ])
    );
  }, [sections]);

  const normalizedTimetables = useMemo(() => {
    return (Array.isArray(allTimetables) ? allTimetables : []).map((timetable) => {
      const matchedSection = sectionLookup.get(String(timetable.section_id));

      return {
        ...timetable,
        class_id: timetable.class_id ?? matchedSection?.class_id ?? null,
        section_name: timetable.section_name ?? matchedSection?.section_name ?? "",
      };
    });
  }, [allTimetables, sectionLookup]);

  const finalFilteredTimetables = normalizedTimetables.filter((t) => {
    if (selectedClass !== "ALL") {
      if (!t.class_id || Number(t.class_id) !== Number(selectedClass)) {
        return false;
      }
    }

    if (selectedSectionFilter !== "ALL") {
      if (Number(t.section_id) !== Number(selectedSectionFilter)) {
        return false;
      }
    }

    return true;
  });

  const sectionSourceHasClass = sections?.some(
    (section) => section?.class_id !== undefined && section?.class_id !== null
  );
  const sectionSource = sectionSourceHasClass
    ? sections
    : Array.from(
      new Map(
        normalizedTimetables.map((item) => [
          item.section_id,
          {
            section_id: item.section_id,
            section_name: item.section_name,
            class_id: item.class_id,
          },
        ])
      ).values()
    );

  const filteredSections = sectionSource.filter((section) => {
    if (selectedClass === "ALL") return true;
    return Number(section.class_id) === Number(selectedClass);
  });

  useEffect(() => {
    if (finalFilteredTimetables.length > 0) {
      const currentSelected = finalFilteredTimetables.find(
        (item) => String(item.section_id) === String(selectedSection)
      );

      if (!currentSelected) {
        setSelectedSection(String(finalFilteredTimetables[0].section_id));
      }
    }
  }, [finalFilteredTimetables, selectedSection]);

  const printDocuments = useMemo(() => {
    const sortedPeriods = [...(Array.isArray(periods) ? periods : [])].sort(
      (a, b) => a.sequence_order - b.sequence_order
    );

    return finalFilteredTimetables.map((sectionTimetable) => ({
      title: getTimetablePrintTitle(sectionTimetable),
      periods: sortedPeriods,
      timetableData: sectionTimetable.timetable || [],
      workingDays: sectionTimetable.working_days,
    }));
  }, [finalFilteredTimetables, periods]);

  if (!schoolId) {
    return (
      <Container fluid className="py-2 px-5">
        <Alert variant="warning">
          Please log in as a school user to manage timetables.
        </Alert>
      </Container>
    );
  }

  const handlePrint = () => {
    if (finalFilteredTimetables.length === 0) {
      alert("No timetable data to print");
      return;
    }

    setShowPrintPreview(true);
  };

  return (
    <div className="px-2 timetable-management-wrapper">
      <TimetablePrintPreviewModal
        show={showPrintPreview}
        onHide={() => setShowPrintPreview(false)}
        documents={printDocuments}
      />
      {/* <GlobalJoyrideCloseButton /> */}
      <Container fluid className="py-3 px-5">
        <Row className="align-items-center mb-2">
          {/* LEFT SIDE */}
          <Col md={6} className="text-start">
           { !isUserRole && (
            <Button
              className="tour-generate-timetable"
              variant="success"
              onClick={handleGenerateTimetable}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    className="me-2"
                  />
                  Generating...
                </>
              ) : (
                <>
                  <Users size={18} className="me-2" />
                  Generate All Timetables
                </>
              )}
            </Button>)}
          </Col>

          {/* RIGHT SIDE */}
          <Col md={6}>
            <div className="d-flex justify-content-end align-items-center gap-2">

              <span className="fw-semibold">Filter by:</span>


              <select
                className="form-select w-auto"
                value={selectedClass}
                onChange={(e) => {
                  setSelectedClass(e.target.value);
                  setSelectedSectionFilter("ALL");
                }}
              >
                <option value="ALL">All Classes</option>
                {classes?.map((cls) => (
                  <option key={cls.class_id} value={cls.class_id}>
                    {cls.class_name}
                  </option>
                ))}
              </select>

              <select
                className="form-select w-auto"
                value={selectedSectionFilter}
                onChange={(e) => setSelectedSectionFilter(e.target.value)}
              >
                <option value="ALL">All Sections</option>
                {filteredSections.map((sec) => (
                  <option key={sec.section_id} value={sec.section_id}>
                    {sec.section_name}
                  </option>
                ))}
              </select>

              <Button
                variant="primary"
                onClick={handlePrint}
                disabled={finalFilteredTimetables.length === 0}
              >
                Print
              </Button>
            </div>
          </Col>
        </Row>

        {/* SECTION TABS */}
        <Row className="mb-1">
          <Col>
            <div
              className="d-flex overflow-auto flex-nowrap gap-2 py-2 timetable-generate"
              style={{ whiteSpace: "nowrap" }}
            >
              {finalFilteredTimetables.map((sectionTimetable) => (
                <Button
                  key={sectionTimetable.section_id}
                  variant={
                    selectedSection === String(sectionTimetable.section_id)
                      ? "primary"
                      : "outline-primary"
                  }
                  style={{
                    minWidth: "90px",
                    borderRadius: "5px",
                    fontWeight: "500",
                  }}
                  onClick={() => handleSectionClick(sectionTimetable.section_id)}
                >
                  {sectionTimetable.section_name}
                </Button>
              ))}
            </div>
          </Col>
        </Row>

        {error && <Alert variant="danger">{error}</Alert>}


        {/* TIMETABLE VIEW */}
        <div
          ref={scrollContainerRef}
          style={{
            overflowY: "auto",
            height: "calc(100vh - 250px)",
            scrollBehavior: "smooth",
          }}
        >
          {allTimetables.length === 0 && !loading && (
            <Alert variant="info">
              No timetable found. Please generate timetables first.
            </Alert>
          )}

          {allTimetables.length > 0 && finalFilteredTimetables.length === 0 && !loading && (
            <Alert variant="info">
              No timetable data matches the selected class/section filters.
            </Alert>
          )}


          <div>
            {finalFilteredTimetables.map((sectionTimetable) => (
              <TimetableView
                key={sectionTimetable.section_id}
                sectionTimetable={sectionTimetable}
                periods={periods}
                timetableData={sectionTimetable.timetable}
                sectionId={sectionTimetable.section_id}
                sectionRefs={sectionRefs}
                workingDays={sectionTimetable.working_days}
              />
            ))}
          </div>
        </div>

      </Container>
    </div>
  );
};

export default GenerateTimetableManagement;